/**
 * Central HTTP client for the BFF (Backend-for-Frontend) layer.
 *
 * Responsibilities:
 *  - Resolve the backend base URL from environment config
 *  - Attach default headers
 *  - Enforce a request timeout
 *  - Normalize success/error responses so callers get a predictable shape
 *
 * All API service modules should go through this client instead of
 * calling fetch() directly, keeping networking concerns in one place.
 */

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

const DEFAULT_TIMEOUT = 15000; // 15s

/**
 * A typed-ish error carrying the HTTP status and any server payload.
 */
export class ApiError extends Error {
  constructor(message, { status = 0, data = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/**
 * Core request function.
 * @param {string} path      - Path appended to the base URL, e.g. "/inquiry"
 * @param {object} options   - fetch options ({ method, body, headers, timeout })
 * @returns {Promise<any>}   - Parsed JSON response body
 */
async function request(path, { method = "GET", body, headers = {}, timeout = DEFAULT_TIMEOUT } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    // Attempt to parse JSON; tolerate empty bodies
    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { message: text };
      }
    }

    if (!response.ok) {
      const message =
        (data && data.message) || `Request failed with status ${response.status}`;
      throw new ApiError(message, { status: response.status, data });
    }

    return data;
  } catch (err) {
    if (err.name === "AbortError") {
      throw new ApiError("Request timed out. Please try again.", { status: 408 });
    }
    if (err instanceof ApiError) {
      throw err;
    }
    // Network / CORS / server down
    throw new ApiError("Unable to reach the server. Please try again later.", {
      status: 0,
    });
  } finally {
    clearTimeout(timer);
  }
}

export const httpClient = {
  get: (path, options) => request(path, { ...options, method: "GET" }),
  post: (path, body, options) => request(path, { ...options, method: "POST", body }),
};
